/**  * A real `POST /snapshot/` response, kept for previewing the snapshot UI in
 * development (see the `/snapshot-preview` route) without running the full
 * assessment flow. Not used in production builds.
 *
 * Sections the page renders only when the payload carries them, and which this
 * particular response happens not to: `what_helped`, `full_picture`, loose
 * `things_to_try`, plus two optional extras the UI supports if the backend
 * starts sending them — `what_i_noticed[].evidence` (`[{ wrote, target }]`,
 * drawn as "fone for phone" pairs) and `what_i_noticed[].stats`
 * (`[{ value, label }]`, drawn as number tiles).
 */
export const snapshotLetterSample = {
  success: true,
  child_id: 'bad5ef52-f131-4859-a997-21d210fb23c9',
  child_name: 'Pranav',
  grade: 'Second',
  tests_completed: ['logic', 'spelling', 'speaking', 'comprehension'],
  snapshot: {
    opening: {
      headline: 'Pranav read all eight sentences without skipping a word.',
      paragraph:
        'In the Voice Challenge, Pranav read every sentence clearly and smoothly. They read at a comfortable pace, a little quicker than is usual for their year. This was a strong demonstration of their ability to read fluently and with expression.',
    },
    what_i_noticed: [
      {
        headline: 'Pranav spells by sound.',
        area_display_name: 'Patterns & Structure',
        signals: ['Spelling by sound'],
        seen_in: ['Word Wizard'],
        badge: 'seen_repeatedly',
        paragraph:
          'Pranav wrote fone for phone, graff for graph, and clunck for clunk. Say those out loud. Every sound is right. Pranav heard each word and wrote down exactly what they heard. What they have not met yet is the rule that some /f/ sounds are spelled ph, and that clunk does not take a c before the k.',
      },
      {
        headline: 'Pranav reads in smooth phrases.',
        area_display_name: 'Language & Expression',
        signals: ['Reading in phrases'],
        seen_in: ['Voice Challenge'],
        badge: 'seen_repeatedly',
        paragraph:
          'Pranav read all eight sentences without skipping a word, at a comfortable pace. They read with smooth phrasing, which is a sign of strong reading fluency. This ability helps in understanding the text as a whole.',
      },
      {
        headline: 'Pranav holds several things in mind.',
        area_display_name: 'Reasoning & Meaning',
        signals: ['Holding several things in mind'],
        seen_in: ['Logic Quest'],
        badge: 'seen_repeatedly',
        paragraph:
          "In Logic Quest, Pranav answered questions like 'Priya is taller than Sam. Sam is taller than Leo. Who is the shortest?' correctly. They showed the ability to hold multiple pieces of information and reason through them.",
      },
      {
        headline: 'Pranav read every word.',
        area_display_name: 'Language & Expression',
        signals: ['Reading every word'],
        seen_in: ['Voice Challenge'],
        badge: 'seen_repeatedly',
        paragraph:
          'Pranav read each sentence in the Voice Challenge without skipping any words. This shows a strong attention to detail and a complete understanding of the text.',
      },
    ],
    still_growing: [
      {
        headline: 'Spelling rules are still emerging.',
        signals: ['Spelling rules'],
        seen_in: ['Word Wizard'],
        paragraph:
          'Pranav wrote fone for phone, graff for graph, and clunck for clunk. They heard the word perfectly. They have not met the rule yet that some /f/ sounds are spelled ph, and that clunk does not take a c before the k.',
        suggestion: {
          title: 'Practice spelling rules at home.',
          body: 'Try using flashcards with common spelling rules, like ph for /f/ sounds.',
          because: "Because Pranav hears the sounds correctly but hasn't met the spelling rules yet.",
        },
      },
      {
        headline: 'Holding on to what the story said is growing.',
        signals: ['Holding on to what the story said'],
        seen_in: ['Story Explorer'],
        paragraph:
          "In 'The Treasure Map', Pranav chose 'The oak tree' for 'What was the X marked next to?' but the answer was 'The tomato plants'. They are still building the habit of holding on to what a story actually said.",
        suggestion: {
          title: 'Re-read and ask questions.',
          body: 'Encourage Pranav to re-read pages and ask who, what, and where questions.',
          because: 'Because this helps reinforce the details of the story.',
        },
      },
      {
        headline: 'Working out a new word is developing.',
        signals: ['Working out a new word'],
        seen_in: ['Story Explorer'],
        paragraph:
          "In 'The Science Fair Project', Pranav chose 'Cold water under the ground' for 'What does 'magma' mean?' but the answer was 'Hot liquid rock inside a volcano'. They are still developing the ability to work out word meanings from context.",
        suggestion: {
          title: 'Discuss new words during reading.',
          body: 'Pause during reading to talk about new words and their meanings.',
          because: 'Because discussing words helps Pranav learn to infer meaning from context.',
        },
      },
      {
        headline: 'Working step by step is emerging.',
        signals: ['Working step by step'],
        seen_in: ['Logic Quest'],
        paragraph:
          "In Logic Quest, Pranav missed 'A farmer puts animals in pens using this rule: animals with 4 legs go in the red pen, animals with 2 legs go in the blue pen. The spider has 8 legs. What happens?' They are learning to work through problems one step at a time.",
        suggestion: {
          title: 'Encourage step-by-step thinking.',
          body: 'Use puzzles and games that require sequential thinking to practice this skill.',
          because: 'Because breaking down problems into steps can help Pranav find solutions.',
        },
      },
    ],
    for_the_conference: {
      headline: 'Three things you could mention at a parent-teacher conference',
      items: [
        {
          point: 'Pranav read all eight sentences in the Voice Challenge without skipping a word.',
          worth_asking: 'Worth mentioning how this fluency supports comprehension in class.',
        },
        {
          point: 'Pranav uses phonetic strategies effectively in spelling.',
          worth_asking: 'Worth asking how phonetic skills are integrated into spelling lessons.',
        },
        {
          point: 'Pranav is developing the ability to hold on to story details.',
          worth_asking: 'Worth asking about strategies used in class to support literal comprehension.',
        },
      ],
    },
    closing:
      "It was a pleasure to see Pranav's strengths in reading and logic. This is just the beginning of a conversation about their learning journey.",
    branding: 'The Dear Parent Project',
    disclaimer:
      'This is a starting point for a conversation about how Pranav learns. It is not a diagnosis or a formal assessment, and it compares them to no one.',
    meta: {
      llm_generated: true,
      tests_completed: ['logic', 'spelling', 'speaking', 'comprehension'],
      guardrails_passed: true,
      voice_slips: [],
      growth_edges_found: 4,
      growth_edges_written: 4,
    },
  },
};

export default snapshotLetterSample;
